import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  Button,
  CircularProgress
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsOff as NotificationsOffIcon
} from '@mui/icons-material';
import { notificationService } from '../../services/notificationService';
import { userService } from '../../services/userService';
import { useAuth } from '../../context/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { Capacitor } from '@capacitor/core';
import { getTokens } from '../../utils/tokenStorage';

const TEST_NOTIFICATION_TIMEOUT_MS = 45000;
const TEST_NOTIFICATION_FUNCTION_URL = 'https://us-central1-waka-running.cloudfunctions.net/testNotification';

const shouldTriggerIosSelfHeal = (message: string): boolean => {
  const text = message.toLowerCase();
  return (
    text.includes('expired') ||
    text.includes('not-registered') ||
    text.includes('registration-token-not-registered') ||
    text.includes('no ios fcm token') ||
    text.includes('no fcm token found')
  );
};

const callTestNotificationDirect = async (userId: string, platform: string) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TEST_NOTIFICATION_TIMEOUT_MS);

  try {
    const tokens = await getTokens();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (tokens?.idToken) {
      headers.Authorization = `Bearer ${tokens.idToken}`;
    }

    const response = await fetch(TEST_NOTIFICATION_FUNCTION_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({ data: { userId, platform } }),
      signal: controller.signal,
    });

    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      const msg = payload?.error?.message || `HTTP ${response.status}`;
      throw new Error(msg);
    }

    if (payload?.error?.message) {
      throw new Error(payload.error.message);
    }
  } finally {
    clearTimeout(timeoutId);
  }
};

const NotificationSettings: React.FC = () => {
  const { user, refreshCurrentUser } = useAuth();
  const userId = user?.id ?? null;
  const [enabled, setEnabled] = useState(() => !!user?.notificationsEnabled);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [notificationStatus, setNotificationStatus] = useState<{
    supported: boolean;
    permission: string | null;
  }>({ supported: false, permission: null });
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const [fetched, status] = await Promise.all([
        userService.getUserById(userId),
        notificationService.getNotificationStatus()
      ]);
      if (cancelled) return;
      setNotificationStatus(status);
      if (fetched != null) setEnabled(!!fetched.notificationsEnabled);
    })();
    return () => { cancelled = true; };
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEnableNotifications = async () => {
    if (!user) return;
    setLoading(true);
    setLoadingStep('Checking permissions...');
    setMessage(null);
    // Overall safety timeout — spinner never stuck forever
    const overallTimeout = setTimeout(() => {
      setLoading(false);
      setLoadingStep('');
      setMessage({ type: 'error', text: 'Request timed out. Please try again.' });
    }, 65000);
    try {
      setLoadingStep('Requesting notification permission...');
      const result = await notificationService.requestPermission(user.id);
      if (result.success) {
        setEnabled(true);
        setLoadingStep('Finalizing...');
        setMessage({
          type: 'success',
          text: '🎉 Notifications enabled! You\'ll receive daily reminders at 8 PM.'
        });
        const status = await notificationService.getNotificationStatus();
        setNotificationStatus(status);
        await refreshCurrentUser();
      } else {
        setMessage({
          type: 'error',
          text: result.error || 'Failed to enable notifications'
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'An error occurred while enabling notifications'
      });
    } finally {
      clearTimeout(overallTimeout);
      setLoading(false);
      setLoadingStep('');
    }
  };

  const handleDisableNotifications = async () => {
    if (!user) return;
    setLoading(true);
    setMessage(null);
    try {
      await notificationService.disableNotifications(user.id);
      setEnabled(false);
      setMessage({
        type: 'info',
        text: 'Notifications disabled. You can re-enable them anytime.'
      });
      await refreshCurrentUser();
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to disable notifications'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      await handleEnableNotifications();
    } else {
      await handleDisableNotifications();
    }
  };

  const handleTestNotification = async () => {
    if (!user) return;
    setTesting(true);
    setMessage(null);
    try {
      console.log('[NotificationSettings] Starting testNotification request...');
      const platform = Capacitor.getPlatform();
      const sendTestNotification = async () => {
        if (platform === 'ios') {
          console.log('[NotificationSettings] iOS detected, using direct HTTP function call');
          await callTestNotificationDirect(user.id, platform);
        } else {
          const testNotification = httpsCallable(functions, 'testNotification');
          await Promise.race([
            testNotification({ userId: user.id, platform }),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Test notification request timed out. Please try again.')), TEST_NOTIFICATION_TIMEOUT_MS)
            ),
          ]);
        }
      };

      try {
        await sendTestNotification();
      } catch (firstError: any) {
        const firstMessage = firstError?.message || '';
        // Self-heal for stale iOS tokens: refresh token and retry once automatically.
        if (platform === 'ios' && shouldTriggerIosSelfHeal(firstMessage)) {
          console.warn('[NotificationSettings] iOS token issue detected, attempting self-heal and retry...');
          setMessage({ type: 'info', text: 'Refreshing iOS notification token and retrying...' });
          await notificationService.refreshNativeToken(user.id);
          await sendTestNotification();
        } else {
          throw firstError;
        }
      }

      console.log('[NotificationSettings] testNotification request resolved');
      setMessage({
        type: 'success',
        text: '✅ Test notification sent! Check your device for the notification.'
      });
    } catch (error: any) {
      console.error('[NotificationSettings] testNotification callable failed:', error);
      let errorMessage = 'Failed to send test notification';
      if (error.message) {
        if (error.message.includes('no FCM token')) {
          errorMessage = 'No FCM token found. Please enable notifications first.';
        } else if (error.message.includes('User not found')) {
          errorMessage = 'User not found. Please try again.';
        } else if (error.message.includes('timed out')) {
          errorMessage = 'Request timed out. Please check your connection and try again.';
        } else {
          errorMessage = error.message;
        }
      }
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setTesting(false);
    }
  };

  if (!notificationStatus.supported) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <NotificationsOffIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="h6">Push Notifications</Typography>
          </Box>
          <Alert severity="warning">
            Push notifications are not supported in this browser. Please use a modern browser like Chrome, Firefox, or Safari.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <NotificationsIcon sx={{ mr: 1, color: enabled ? 'primary.main' : 'text.secondary' }} />
          <Typography variant="h6">Push Notifications</Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Get reminded daily at 8:00 PM to log your activity and maintain your streak!
        </Typography>

        {message && (
          <Alert severity={message.type} sx={{ mb: 2 }}>
            {message.text}
          </Alert>
        )}

        {notificationStatus.permission === 'denied' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            You have blocked notifications for this site. To enable them, please update your browser settings.
          </Alert>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={handleToggle}
              disabled={loading || notificationStatus.permission === 'denied'}
            />
          }
          label={
            <Box>
              <Typography variant="body1">
                {enabled ? 'Notifications Enabled' : 'Enable Daily Reminders'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Sends at 8:00 PM if you haven't logged your activity
              </Typography>
            </Box>
          }
        />

        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {loadingStep || 'Setting up notifications...'}
            </Typography>
          </Box>
        )}

        {notificationStatus.permission === 'default' && !enabled && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleEnableNotifications}
              disabled={loading}
              startIcon={<NotificationsIcon />}
            >
              Enable Notifications
            </Button>
          </Box>
        )}

        {enabled && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              onClick={handleTestNotification}
              disabled={testing || loading}
              startIcon={<NotificationsIcon />}
            >
              {testing ? 'Sending Test...' : 'Send Test Notification'}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Test if notifications are working by sending a test notification to your device
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>How it works:</strong><br />
            • Reminder sent at 8:00 PM if you haven't logged today<br />
            • Helps maintain your streak and earn points<br />
            • You can disable notifications anytime
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default NotificationSettings;
